import React from "react";

import {
  BROKER_ALERT_CLASS,
  BROKER_ALERT_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
  BrokerAlertIcon,
} from "@/components/Broker/brokerAlertUi";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { EnrichedPreviewError, PreviewErrorSummary } from "../../../utils/customCsvPreviewDiagnostics";
import {
  CUSTOM_CSV_FIELD_CARD_CLASS,
  CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS,
} from "./customCsvWizardUi";

export interface PreviewErrorsPanelProps {
  enrichedErrors: EnrichedPreviewError[];
  summaries: PreviewErrorSummary[];
}

const TABLE_HEAD_CLASS =
  "font-[family-name:var(--font-primary)] text-xs font-normal normal-case tracking-normal text-[color:var(--brand-tropical-surf)]";

const TABLE_CELL_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal text-gray-500";

const CODE_CLASS =
  "rounded-[4px] bg-muted px-1.5 py-0.5 font-mono text-xs text-gray-600";

const PreviewErrorsPanel = ({
  enrichedErrors,
  summaries,
}: PreviewErrorsPanelProps): React.ReactElement => {
  return (
    <div className="flex flex-col gap-5">
      {summaries.length > 0 ? (
        <Field className="gap-2">
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>What went wrong</FieldLabel>
          <div className="flex flex-col gap-2">
            {summaries.map((summary) => (
              <Alert key={summary.key} variant="error" className={BROKER_ALERT_CLASS}>
                <BrokerAlertIcon tone="error" />
                <AlertTitle className={BROKER_ALERT_TITLE_CLASS}>
                  {summary.count > 1
                    ? `${summary.count} rows: ${summary.tesseraFieldLabel}`
                    : `Row ${summary.exampleRow}: ${summary.tesseraFieldLabel}`}
                </AlertTitle>
                <AlertDescription className={cn(BROKER_ALERT_DESC_CLASS, "space-y-2")}>
                  <p className="m-0">{summary.issue}</p>
                  <p className="m-0 text-xs">
                    Example: column{" "}
                    <span className={BROKER_ALERT_TITLE_CLASS}>{summary.exampleColumn}</span> ={" "}
                    <span className={BROKER_ALERT_TITLE_CLASS}>{summary.exampleValue}</span>
                    {summary.exampleRow > 0 ? ` (row ${summary.exampleRow})` : null}
                  </p>
                  {summary.fixHint ? <p className="m-0 text-xs">{summary.fixHint}</p> : null}
                </AlertDescription>
              </Alert>
            ))}
          </div>
        </Field>
      ) : null}

      <Card
        data-custom-csv-preview-card=""
        className={cn(CUSTOM_CSV_FIELD_CARD_CLASS, "w-full")}
      >
        <CardContent className={CUSTOM_CSV_FIELD_CARD_CONTENT_CLASS}>
          <Field className="gap-2">
            <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Error details</FieldLabel>
            <FieldDescription className={BROKER_ALERT_DESC_CLASS}>
              Row-level issues from the preview run.
            </FieldDescription>
            <div
              data-shadcn-data-table
              data-custom-csv-preview-errors-table=""
              className="w-full overflow-x-auto overflow-y-hidden rounded-[6px] border border-border bg-background"
            >
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className={TABLE_HEAD_CLASS}>Row</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Tessera field</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Your column</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Value in file</TableHead>
                    <TableHead className={TABLE_HEAD_CLASS}>Problem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrichedErrors.map((err, index) => (
                    <TableRow key={`${err.row}-${err.tesseraField}-${index}`}>
                      <TableCell className={TABLE_CELL_CLASS}>{err.row ?? "—"}</TableCell>
                      <TableCell className={TABLE_CELL_CLASS}>{err.tesseraFieldLabel}</TableCell>
                      <TableCell>
                        <code className={CODE_CLASS}>{err.columnHeader}</code>
                      </TableCell>
                      <TableCell className="max-w-[14rem]">
                        <code className={cn(CODE_CLASS, "block truncate")} title={err.rawValue}>
                          {err.rawValue}
                        </code>
                      </TableCell>
                      <TableCell className={TABLE_CELL_CLASS}>
                        {err.errorCode ? (
                          <code className={cn(CODE_CLASS, "mb-1 inline-block")}>
                            {err.errorCode}
                          </code>
                        ) : null}
                        <p className="m-0">{err.issue}</p>
                        {err.fixHint ? (
                          <p className={cn(BROKER_ALERT_DESC_CLASS, "m-0 mt-1 text-xs")}>
                            {err.fixHint}
                          </p>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Field>
        </CardContent>
      </Card>
    </div>
  );
};

export default PreviewErrorsPanel;
